"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { X, Download } from "lucide-react";

interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
const DISMISS_KEY = "el-install-dismissed";

/** A quiet, in-flow home hint. Never obscure navigation or claim offline course support. */
export default function InstallPrompt() {
  const pathname = usePathname();
  const [deferred, setDeferred] = useState<InstallEvent | null>(null);
  const [ios, setIos] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      (navigator as unknown as { standalone?: boolean }).standalone
    )
      return;
    let suppressed = false;
    try {
      suppressed = Date.now() - Number(localStorage.getItem(DISMISS_KEY) || 0) < 7 * 86400000;
    } catch {
      /* hint remains dismissible in memory */
    }
    if (suppressed) return;
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as InstallEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    const timer = window.setTimeout(() => {
      setIos(/iPad|iPhone|iPod/.test(navigator.userAgent));
      setAllowed(true);
    }, 12000);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);
  if (pathname !== "/" || !allowed || hidden || (!deferred && !ios)) return null;
  function dismiss() {
    setHidden(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignored */
    }
  }
  return (
    <aside aria-label="添加到桌面" className="border-b bg-muted/50 px-4 py-3">
      <div className="mx-auto flex max-w-5xl items-start gap-3 text-sm">
        <Download className="mt-3 h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">下次从桌面直接开始</p>
          <p className="mt-1 text-xs leading-6 text-muted-foreground">
            {ios ? "Safari：分享 → 添加到主屏幕。学习内容仍需联网。" : "添加桌面入口，学习内容仍需联网。"}
          </p>
          {error && (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          )}
        </div>
        {deferred && (
          <button
            className="min-h-11 rounded-lg border bg-card px-3 text-xs font-semibold"
            onClick={async () => {
              try {
                await deferred.prompt();
                const result = await deferred.userChoice;
                if (result.outcome === "accepted") setHidden(true);
                else dismiss();
              } catch {
                setError("暂时无法安装，可通过浏览器菜单添加到桌面。");
              }
            }}
          >
            添加
          </button>
        )}
        <button
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-accent"
          onClick={dismiss}
          aria-label="七天内不再提示安装"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
}
